import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderTitle,
  Text,
  Badge,
} from '@antoree/ant-ui';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useRedirect } from 'hooks';
import { useGetSessionsMissingEvaluation } from 'services/session/getSessionsMissingEvaluation';
import { CourseWithWarnings } from 'services/session/getSessionsMissingEvaluation';

type Props = {
  close: () => void;
};

// Helper functions
export const getWarningText = (session: any) => {
  if (session.warningText) {
    return session.warningText;
  }
  // Fallback for old format
  if (session.type === 3) {
    return (
      <FormattedMessage
        id="warning_notification.missing_evaluation"
        defaultMessage="Missing Evaluation"
      />
    );
  }
  if (session.testRequired && !session.test) {
    return (
      <FormattedMessage
        id="warning_notification.missing_test_30h"
        defaultMessage="Missing Test 30h"
      />
    );
  }
  return (
    <FormattedMessage
      id="warning_notification.missing_content"
      defaultMessage="Missing Content"
    />
  );
};

export const getWarningType = (session: any) => {
  if (session.warningType) {
    return session.warningType;
  }
  // Fallback for old format
  if (session.type === 3) {
    return 'evaluation';
  }
  if (session.testRequired && !session.test) {
    return 'test';
  }
  return 'content';
};

export const WarningNotificationHeader = ({
  courses,
}: {
  courses: CourseWithWarnings[];
}) => {
  const totalWarnings = courses.reduce(
    (sum, course) => sum + course.warningCount,
    0,
  );

  return (
    <>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .shake-animation {
          animation: shake 0.5s ease-in-out;
          animation-iteration-count: 3;
        }
        .warning-highlight {
          background-color:rgb(255, 255, 255);
          border-left: 4px solid;
          padding: 8px 12px;
          border-radius: 4px;
        }
      `}</style>
      <ModalHeader>
        <ModalHeaderTitle>
          <Text className="text-xl font-semibold" style={{ color: '#d32f2f' }}>
            <FormattedMessage
              id="warning_notification.incomplete_sessions_title"
              defaultMessage="Incomplete Sessions"
            />
            {totalWarnings > 0 && (
              <Badge
                color="danger"
                className="ml-2 shake-animation"
                style={{
                  fontSize: '14px',
                  padding: '4px 8px',
                  fontWeight: 'bold',
                }}
              >
                {totalWarnings}
              </Badge>
            )}
          </Text>
        </ModalHeaderTitle>
      </ModalHeader>
    </>
  );
};

export const WarningNotificationContent = ({
  courses,
}: {
  courses: CourseWithWarnings[];
}) => {
  const handleGoToCourse = (
    courseId: number,
    firstWarningSessionId?: number | string,
  ) => {
    let url = `/courses/${courseId}/sessions`;
    const params = new URLSearchParams();
    if (firstWarningSessionId) {
      params.append('sessionId', String(firstWarningSessionId));
    }
    // Add status=3 to filter sessions faster
    params.append('status', '3');
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    // Open in new tab
    const fullUrl = window.location.origin + url;
    window.open(fullUrl, '_blank');
  };

  const totalWarnings = courses.reduce(
    (sum, course) => sum + course.warningCount,
    0,
  );

  return (
    <div className="space-y-4">
      <div className="warning-highlight">
        <Text className="text-sm" style={{ color: '#d32f2f', fontWeight: 600 }}>
          <FormattedMessage
            id="warning_notification.incomplete_sessions_desc"
            defaultMessage="{count} session{count, plural, one {} other {s}} need to be completed to be counted for salary."
            values={{ count: totalWarnings }}
          />
        </Text>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {courses.map((course: CourseWithWarnings) => (
          <div
            key={course.id}
            className="border-2 border-gray-300 rounded-lg p-5 shadow-sm bg-white"
            style={{ minWidth: '100%' }}
          >
            <div className="mb-3">
              <div className="flex justify-between items-center">
                <div className="flex-1 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Text className="text-xs text-gray-500">
                      <FormattedMessage
                        id="warning_notification.course_id"
                        defaultMessage="Course ID:"
                      />
                    </Text>
                    <Text
                      className="text-sm font-semibold"
                      style={{ color: '#d32f2f' }}
                    >
                      #{course.id}
                    </Text>
                  </div>
                  <div className="flex items-center gap-1">
                    <Text className="text-xs text-gray-500">
                      <FormattedMessage
                        id="warning_notification.course_title"
                        defaultMessage="Title:"
                      />
                    </Text>
                    <Text
                      className="text-sm font-semibold"
                      style={{ color: '#d32f2f' }}
                    >
                      {course.title}
                    </Text>
                  </div>
                  {course.student && (
                    <div className="flex items-center gap-1">
                      <Text className="text-xs text-gray-500">
                        <FormattedMessage
                          id="warning_notification.student"
                          defaultMessage="Student:"
                        />
                      </Text>
                      <Text className="text-sm" style={{ color: '#666' }}>
                        {course.student.name || course.student.shown_name}
                      </Text>
                    </div>
                  )}
                </div>
                <Badge
                  color="danger"
                  className="ml-2 shake-animation"
                  style={{
                    fontSize: '13px',
                    padding: '5px 9px',
                    fontWeight: 'bold',
                  }}
                >
                  {course.warningCount}
                </Badge>
              </div>
            </div>

            <div className="mt-4">
              <table
                className="w-full border-collapse"
                style={{ fontSize: '13px', tableLayout: 'fixed' }}
              >
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <thead>
                  <tr
                    style={{
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #ddd',
                    }}
                  >
                    <th
                      className="text-left py-3 px-4 font-semibold text-gray-700"
                      style={{
                        borderRight: '1px solid #ddd',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FormattedMessage
                        id="warning_notification.session_id"
                        defaultMessage="Session ID"
                      />
                    </th>
                    <th
                      className="text-left py-3 px-4 font-semibold text-gray-700"
                      style={{
                        borderRight: '1px solid #ddd',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FormattedMessage
                        id="warning_notification.occurred_at"
                        defaultMessage="Date"
                      />
                    </th>
                    <th
                      className="text-left py-3 px-4 font-semibold text-gray-700"
                      style={{
                        borderRight: '1px solid #ddd',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FormattedMessage
                        id="warning_notification.reason"
                        defaultMessage="Reason"
                      />
                    </th>
                    <th
                      className="text-left py-3 px-4 font-semibold text-gray-700"
                      style={{
                        borderRight: '1px solid #ddd',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FormattedMessage
                        id="warning_notification.penalty"
                        defaultMessage="Penalty"
                      />
                    </th>
                    <th
                      className="text-left py-3 px-4 font-semibold text-gray-700"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <FormattedMessage
                        id="warning_notification.action"
                        defaultMessage="Action"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {course.warningSessions.map(session => {
                    const warningText = getWarningText(session);
                    const warningType = getWarningType(session);
                    // Use penaltyInfo from API if available, otherwise fallback
                    let penaltyInfo: React.ReactNode = session.penaltyInfo;
                    if (!penaltyInfo) {
                      if (
                        warningType === 'evaluation' ||
                        warningType === 'test'
                      ) {
                        penaltyInfo = (
                          <FormattedMessage
                            id="warning_notification.no_salary"
                            defaultMessage="No salary for this session"
                          />
                        );
                      } else {
                        penaltyInfo = (
                          <FormattedMessage
                            id="warning_notification.penalty_70"
                            defaultMessage="Will be penalized 70% salary"
                          />
                        );
                      }
                    }

                    // Get unit_id (remove _content or _test suffix if exists)
                    const sessionIdStr = String(session.id);
                    let unitId = session.unit_id;
                    if (!unitId) {
                      if (
                        sessionIdStr.includes('_content') ||
                        sessionIdStr.includes('_test')
                      ) {
                        unitId = parseInt(sessionIdStr.split('_')[0]);
                      } else {
                        unitId =
                          typeof session.id === 'number'
                            ? session.id
                            : parseInt(sessionIdStr);
                      }
                    }

                    // Different colors and styles for different warning types
                    let borderColor = '#f57c00';
                    let bgColor = '#fff3e0';
                    let badgeColor = '#f57c00';
                    let badgeText = 'CONTENT';

                    if (warningType === 'evaluation') {
                      borderColor = '#d32f2f';
                      bgColor = '#ffebee';
                      badgeColor = '#d32f2f';
                      badgeText = 'EVAL';
                    } else if (warningType === 'test') {
                      borderColor = '#9c27b0';
                      bgColor = '#f3e5f5';
                      badgeColor = '#9c27b0';
                      badgeText = 'TEST';
                    }

                    return (
                      <tr
                        key={session.id}
                        className="border-b border-gray-200"
                        style={{
                          borderLeft: `4px solid ${borderColor}`,
                          backgroundColor: bgColor,
                          minHeight: '60px',
                        }}
                      >
                        <td
                          className="py-3 px-4 align-top"
                          style={{
                            borderRight: '1px solid #eee',
                            width: '12%',
                          }}
                        >
                          <div className="flex flex-col gap-1">
                            <Badge
                              style={{
                                backgroundColor: badgeColor,
                                color: 'white',
                                fontSize: '10px',
                                padding: '4px 10px',
                                fontWeight: 'bold',
                                width: 'fit-content',
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                minWidth: 'fit-content',
                              }}
                            >
                              {badgeText}
                            </Badge>
                            <Text
                              className="text-xs font-semibold mt-1"
                              style={{ color: '#d32f2f', whiteSpace: 'nowrap' }}
                            >
                              #{unitId}
                            </Text>
                          </div>
                        </td>
                        <td
                          className="py-3 px-4 align-top"
                          style={{
                            borderRight: '1px solid #eee',
                            width: '12%',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Text
                            className="text-xs"
                            style={{ color: '#c62828', fontWeight: 500 }}
                          >
                            {session.occurredAt
                              ? new Date(session.occurredAt).toLocaleDateString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  },
                                )
                              : 'N/A'}
                          </Text>
                        </td>
                        <td
                          className="py-3 px-4 align-top"
                          style={{
                            borderRight: '1px solid #eee',
                            width: '32%',
                          }}
                        >
                          <div className="flex flex-col gap-1">
                            <Text
                              className="text-sm"
                              style={{
                                color: '#d32f2f',
                                fontWeight: 600,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: '1.5',
                              }}
                            >
                              {warningText}
                            </Text>
                            {session.testTitle && warningType === 'test' && (
                              <Text
                                className="text-xs"
                                style={{
                                  color: '#7b1fa2',
                                  fontStyle: 'italic',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {session.testTitle}
                              </Text>
                            )}
                          </div>
                        </td>
                        <td
                          className="py-3 px-4 align-top"
                          style={{
                            borderRight: '1px solid #eee',
                            width: '26%',
                          }}
                        >
                          <Text
                            className="text-xs"
                            style={{
                              color: '#c62828',
                              fontWeight: 600,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              lineHeight: '1.5',
                            }}
                          >
                            {penaltyInfo}
                          </Text>
                        </td>
                        <td
                          className="py-3 px-4 align-top"
                          style={{ width: '20%', whiteSpace: 'nowrap' }}
                        >
                          <Button
                            onClick={() => {
                              handleGoToCourse(course.id, unitId);
                            }}
                            fill
                            style={{
                              padding: '6px 16px',
                              fontSize: '12px',
                              minWidth: '80px',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            <Text
                              style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                            >
                              <FormattedMessage
                                id="common.view"
                                defaultMessage="View"
                              />
                            </Text>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WarningNotification = ({ close }: Props) => {
  const { redirectTo } = useRedirect();
  const { data, isLoading } = useGetSessionsMissingEvaluation({
    pageSize: 50,
    page: 1,
  });

  const courses = data?.data?.courses || [];

  if (isLoading) {
    return (
      <Modal onClose={close} style={{ minWidth: '800px', maxWidth: '900px' }}>
        <style>{`
          @keyframes fadeInSlide {
            0% {
              opacity: 0;
              transform: translateX(-20px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .check-step-1 {
            animation: fadeInSlide 0.5s ease-out forwards;
            opacity: 0;
          }
          .check-step-2 {
            animation: fadeInSlide 0.5s ease-out 0.8s forwards;
            opacity: 0;
          }
          .check-step-3 {
            animation: fadeInSlide 0.5s ease-out 1.6s forwards;
            opacity: 0;
          }
          .spinner-dot {
            animation: spin 1s linear infinite;
          }
        `}</style>
        <ModalHeader>
          <ModalHeaderTitle>
            <Text
              className="text-xl font-semibold"
              style={{ color: '#d32f2f' }}
            >
              <FormattedMessage
                id="warning_notification.checking_incomplete_sessions"
                defaultMessage="Checking Incomplete Sessions..."
              />
            </Text>
          </ModalHeaderTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 check-step-1">
                <div className="relative">
                  <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full spinner-dot"></div>
                </div>
                <Text className="text-sm text-gray-700 font-medium">
                  <FormattedMessage
                    id="warning_notification.checking_missing_evaluation"
                    defaultMessage="Checking missing Evaluation..."
                  />
                </Text>
              </div>
              <div className="flex items-center space-x-3 check-step-2">
                <div className="relative">
                  <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full spinner-dot"></div>
                </div>
                <Text className="text-sm text-gray-700 font-medium">
                  <FormattedMessage
                    id="warning_notification.checking_missing_content"
                    defaultMessage="Checking missing Content..."
                  />
                </Text>
              </div>
              <div className="flex items-center space-x-3 check-step-3">
                <div className="relative">
                  <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full spinner-dot"></div>
                </div>
                <Text className="text-sm text-gray-700 font-medium">
                  <FormattedMessage
                    id="warning_notification.checking_missing_test"
                    defaultMessage="Checking missing Test..."
                  />
                </Text>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 check-step-3">
              <Text className="text-xs text-gray-500 italic">
                <FormattedMessage
                  id="warning_notification.checking_wait_message"
                  defaultMessage="Please wait while we check all your sessions. This may take a few seconds..."
                />
              </Text>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50 check-step-3">
              <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-40 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-48 animate-pulse"></div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="h-8 bg-gray-200 rounded animate-pulse"></th>
                    <th className="h-8 bg-gray-200 rounded animate-pulse"></th>
                    <th className="h-8 bg-gray-200 rounded animate-pulse"></th>
                    <th className="h-8 bg-gray-200 rounded animate-pulse"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="h-12 bg-gray-100 rounded animate-pulse"></td>
                    <td className="h-12 bg-gray-100 rounded animate-pulse"></td>
                    <td className="h-12 bg-gray-100 rounded animate-pulse"></td>
                    <td className="h-12 bg-gray-100 rounded animate-pulse"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  if (courses.length === 0) {
    return (
      <Modal onClose={close}>
        <ModalHeader>
          <ModalHeaderTitle>
            <Text className="text-2xl">
              <FormattedMessage
                id="warning_notification.title"
                defaultMessage="Notification"
              />
            </Text>
          </ModalHeaderTitle>
        </ModalHeader>
        <ModalBody>
          <Text className="text-success">
            <FormattedMessage
              id="warning_notification.no_warnings"
              defaultMessage="Great! You have no courses that need warnings."
            />
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={close} fill className="ml-auto">
            <Text>
              <FormattedMessage id="common.close" defaultMessage="Close" />
            </Text>
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal onClose={close} style={{ minWidth: '800px', maxWidth: '900px' }}>
      <WarningNotificationHeader courses={courses} />
      <ModalBody>
        <WarningNotificationContent courses={courses} />
      </ModalBody>
      <ModalFooter>
        <Button onClick={close} fill>
          <Text>
            <FormattedMessage id="common.close" defaultMessage="Close" />
          </Text>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WarningNotification;
